/**
 * Build `state.thread` for React Router after POST /api/v2/posts (UnifiedPostComposer).
 * Shape matches Community feed rows (normalizeForumRowToThread) / canonical thread navigation helpers.
 */

import { normalizeForumTagsForThread } from "./forumFeedTagUtils";

/**
 * @param {{ post?: object, attachments?: object[] } | null | undefined} result
 * @param {{ username?: string, name?: string, profile_picture?: string, profilePicture?: string } | null | undefined} viewer
 * @returns {object | null} Thread-shaped object for `navigate(..., { state: { thread } })` (canonical shell).
 */
export function mapV2CreateResultToThreadNavState(result, viewer) {
  const post = result?.post && typeof result.post === "object" ? result.post : null;
  if (!post) return null;

  const pid = post.post_id ?? post.id;
  const idNum = Number(pid);
  if (!Number.isFinite(idNum) || idNum < 1) return null;

  const fmt = String(post.content_format || "").trim();
  const titleStr = String(post.title || "").trim();
  const contentStr = String(post.content || "").trim();
  let title = post.title || "";
  let body = post.content || "";
  if (fmt === "club_title_body_v1") {
    title = titleStr;
    body = contentStr && contentStr !== titleStr ? contentStr : "";
  }

  const createdAt = post.created_at || new Date().toISOString();
  const displayName =
    String(viewer?.username || viewer?.name || "").trim() || "You";

  /** @type {{ type: string, data: object } | null} */
  let attachment = null;

  if (post.attachment_type) {
    const attachmentData = {};
    if (post.attachment_type === "anime" && post.attachment_anime_id) {
      attachmentData.id = post.attachment_anime_id;
      attachmentData.title = post.anime_title || "Anime";
      attachmentData.image_url = post.anime_image_url || "";
      attachmentData.episodes = post.anime_episodes ?? null;
    } else if (post.attachment_type === "artwork" && post.attachment_artwork_id) {
      attachmentData.id = post.attachment_artwork_id;
      attachmentData.title = post.artwork_title || "Artwork";
      attachmentData.image_url = post.artwork_image_url || "";
    } else if (post.attachment_type === "image" && post.attachment_image_url) {
      attachmentData.url = post.attachment_image_url;
      attachmentData.preview = post.attachment_image_url;
    }
    if (Object.keys(attachmentData).length > 0) {
      attachment = { type: post.attachment_type, data: attachmentData };
    }
  }

  if (!attachment && post.attachment_image_url) {
    const u = String(post.attachment_image_url).trim();
    if (u) {
      attachment = { type: "image", data: { url: u, preview: u } };
    }
  }

  if (!attachment && Array.isArray(result?.attachments)) {
    const img = result.attachments.find(
      (a) => a && String(a.kind || "").toLowerCase() === "image" && String(a.url || "").trim()
    );
    if (img?.url) {
      const u = String(img.url).trim();
      attachment = { type: "image", data: { url: u, preview: u } };
    }
  }

  const clubIdNum = Number(post.club_id ?? post.clubId);
  const clubSlugStr =
    post.club_slug != null && String(post.club_slug).trim() !== ""
      ? String(post.club_slug).trim()
      : "";

  const out = {
    id: String(idNum),
    author: displayName,
    authorId: post.user_id,
    avatar: viewer?.profile_picture ?? viewer?.profilePicture ?? null,
    createdAt,
    displayTime: post.created_at ? new Date(post.created_at).toLocaleString() : new Date(createdAt).toLocaleString(),
    title,
    body,
    likes: 0,
    replies: 0,
    rating: post.rating ?? null,
    topicId: post.topic || post.topic_id || "other",
    tags: normalizeForumTagsForThread(post.tags),
    ...(Number.isFinite(clubIdNum) && clubIdNum > 0 ? { clubId: clubIdNum, club_id: clubIdNum } : {}),
    ...(clubSlugStr ? { clubSlug: clubSlugStr, club_slug: clubSlugStr } : {}),
  };

  if (attachment) {
    out.attachment = attachment;
  }

  return out;
}

/**
 * @param {{ post?: object } | null | undefined} result
 * @returns {number | null}
 */
export function getV2CreatePostId(result) {
  const post = result?.post && typeof result.post === "object" ? result.post : null;
  if (!post) return null;
  const n = Number(post.post_id ?? post.id);
  return Number.isFinite(n) && n >= 1 ? n : null;
}
